import React, { useEffect, useRef, useState } from "react";
import { useLoaderData, useNavigate, Form } from "react-router-dom";
import { getEntities, getHotSheet, addHotSheet, updateHotSheet, deleteHotSheet } from "../../API";
import { PROVINCES } from "../../Constants";
import { assert, StructError } from "superstruct";
import { hotSheetSchema } from "../../util/schemas";
import InputFormField from "../../components/InputFormField";
import SelectFormField from "../../components/SelectFormField";
import ItemsSelector from "../../components/ItemsSelector";
import UpdateDeleteBar from "../../components/UpdateDeleteBar";
import Priorities from "../../components/Priorities";
import MonthFormField from "../../components/MonthFormField";

function toPickerMonth(month) {
  const now = new Date();
  if (!month) {
    return [{ month: now.getMonth() + 1, year: now.getFullYear(), monthName: now.toLocaleString("en", { month: "short" }) }];
  }
  const [yearStr, monthStr] = month.split("-");
  const year = parseInt(yearStr);
  const m = parseInt(monthStr);
  return [{ month: m, year, monthName: new Date(year, m - 1).toLocaleString("en", { month: "short" }) }];
}

function fromPickerMonth(months) {
  if (!months || months.length === 0) return "";
  const { month, year } = months[0];
  return `${year}-${String(month).padStart(2, "0")}`;
}

export async function loader({ params }) {
  const [manufacturersResp, territoriesResp, bannersResp, representativesResp] =
    await Promise.all([
      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
      getEntities("territories", "?size=1000"),
      getEntities("banners", "?size=1000"),
      getEntities("representatives", "?size=1000"),
    ]);

  const allManufacturers = manufacturersResp?.content ?? [];
  const allTerritories = territoriesResp?.content ?? [];
  const allBanners = bannersResp?.content ?? [];
  const allRepresentatives = representativesResp?.content ?? [];

  let hotSheet;
  if (params.id) {
    hotSheet = await getHotSheet(params.id);
    // all fields come from the JSON file (stores, representatives, territories, banners, provinces, priorities)
    hotSheet.months = toPickerMonth(hotSheet.month);
    // convert HotSheetRepresentative objects back to full Representative objects (like task loader does)
    hotSheet.representatives = (hotSheet.representatives ?? [])
      .map((r) => {
        const repId = r.hotSheetRepresentativeKey?.representativeId ?? r.id ?? 0;
        return allRepresentatives.find((rep) => rep.id === repId);
      })
      .filter(Boolean);
    // convert HotSheetStore objects to {id} for modal selection matching
    hotSheet.stores = (hotSheet.stores ?? []).map((s) => ({
      id: s.hotSheetStoreKey?.storeId ?? s.id ?? 0,
    }));
  } else {
    hotSheet = {
      name: "",
      months: toPickerMonth(null),
      manufacturer: null,
      provinces: [],
      territories: [],
      banners: [],
      stores: [],
      representatives: [],
      priorities: [],
    };
  }

  return { hotSheet, allManufacturers, allTerritories, allBanners, allRepresentatives };
}

function hasErrors(hotSheet) {
  try {
    assert(hotSheet, hotSheetSchema);
  } catch (err) {
    if (err instanceof StructError) {
      return err.failures().reduce((acc, { key, message }) => ({ ...acc, [key]: message }), {});
    }
  }
  return null;
}

function HotSheet() {
  const { hotSheet: initialHotSheet, allManufacturers, allTerritories, allBanners, allRepresentatives } =
    useLoaderData();

  const navigate = useNavigate();
  const refForm = useRef();

  const [hotSheet, setHotSheet] = useState(initialHotSheet);
  const [priorities, setPriorities] = useState(initialHotSheet.priorities ?? []);
  const [errors, setErrors] = useState({});

  const [filteredTerritories, setFilteredTerritories] = useState([]);
  const [filteredRepresentatives, setFilteredRepresentatives] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);

  useEffect(() => {
    handleProvinceSelection(hotSheet.provinces, false);
    handleTerritorySelection(hotSheet.territories, false);
    handleBannerSelection(hotSheet.banners, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProvinceSelection = (provinces, updateState = true) => {
    const territories = allTerritories.filter((t) =>
      provinces.find((p) => p.id == t.province?.id)
    );
    setFilteredTerritories(territories);
    if (updateState) {
      const hs = { ...hotSheet, provinces, territories: [], representatives: [], banners: [], stores: [] };
      setHotSheet(hs);
      setFilteredRepresentatives([]);
      setFilteredBanners([]);
      setFilteredStores([]);
    }
  };

  const handleTerritorySelection = (territories, updateState = true) => {
    const reps = allRepresentatives.filter((r) =>
      territories.find((t) => (r.territories ?? []).find((t2) => t2.id == t.id))
    );
    const banners = allBanners.filter((b) =>
      territories.find((t) => (b.provinces ?? []).find((p) => p.id == t.province?.id))
    );
    setFilteredRepresentatives(reps);
    setFilteredBanners(banners);
    setFilteredStores([]);
    if (updateState) {
      setHotSheet({ ...hotSheet, territories, representatives: [], banners: [], stores: [] });
    }
  };

  const handleBannerSelection = (banners, updateState = true) => {
    if (updateState) {
      setHotSheet({ ...hotSheet, banners, stores: [] });
    }
    if (banners.length === 0) { setFilteredStores([]); return; }
    const bannerIds = banners.map((b) => `bannerIds=${b.id}`).join("&");
    const territoryIds = hotSheet.territories.map((t) => `territoryIds=${t.id}`).join("&");
    const params = `?${bannerIds}&${territoryIds}&size=1000`;
    getEntities("stores/by-banners-and-territories", params).then((resp) =>
      setFilteredStores(resp?.content ?? [])
    );
  };

  const addPriority = () =>
    setPriorities([...priorities, { no: priorities.length + 1, text: "", subPriorities: [] }]);

  const updatePriority = (p) => {
    const idx = priorities.findIndex((_p) => _p.no === p.no);
    const next = [...priorities];
    next.splice(idx, 1, p);
    setPriorities(next);
  };

  const deletePriority = (p) => {
    const filtered = priorities.filter((_p) => _p.no !== p.no).map((x, i) => ({ ...x, no: i + 1 }));
    setPriorities(filtered);
  };

  const submit = () =>
    refForm.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));

  const itemSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get("name");
    const month = fromPickerMonth(hotSheet.months);

    const validationErrors = hasErrors({
      name,
      month,
      manufacturer: hotSheet.manufacturer,
      provinces: hotSheet.provinces,
      territories: hotSheet.territories,
      representatives: hotSheet.representatives,
      banners: hotSheet.banners,
    });
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const payload = {
      name,
      month,
      manufacturer: hotSheet.manufacturer ?? null,
      stores: (hotSheet.stores ?? []).map((s) => ({
        hotSheetStoreKey: { storeId: s.id ?? s.hotSheetStoreKey?.storeId ?? 0 },
      })),
      representatives: (hotSheet.representatives ?? []).map((r) => ({
        hotSheetRepresentativeKey: { representativeId: r.id ?? r.hotSheetRepresentativeKey?.representativeId ?? 0 },
      })),
      territories: hotSheet.territories,
      banners: hotSheet.banners,
      provinces: hotSheet.provinces,
      priorities,
    };

    if (hotSheet.id) {
      await updateHotSheet(hotSheet.id, payload);
    } else {
      await addHotSheet(payload);
    }
    navigate("../hot-sheets");
  };

  const itemDelete = async () => {
    await deleteHotSheet(hotSheet.id);
    navigate("../hot-sheets");
  };

  const isEdit = !!hotSheet.id;

  return (
    <div className="flex-grow-1 overflow-auto">
      <UpdateDeleteBar onDelete={isEdit ? itemDelete : undefined} onSave={submit} title={hotSheet.name || "New Hot Sheet"} />
      <div className="container-fluid py-4 px-4">
        <Form onSubmit={itemSave} ref={refForm}>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-8">
                  <InputFormField label="Name" name="name" value={hotSheet.name} error={errors.name} />
                </div>
                <div className="col-md-4">
                  <MonthFormField
                    label="Month"
                    value={hotSheet.months}
                    onChange={(months) => setHotSheet({ ...hotSheet, months })}
                    error={errors.month}
                  />
                </div>
                <div className="col-md-6">
                  <SelectFormField
                    label="Provinces"
                    value={hotSheet.provinces}
                    options={PROVINCES}
                    isMulti="true"
                    onChange={handleProvinceSelection}
                    objectValue={true}
                    error={errors.provinces}
                  />
                </div>
                <div className="col-md-6">
                  <SelectFormField
                    label="Manufacturer"
                    value={hotSheet.manufacturer}
                    options={allManufacturers}
                    onChange={(m) => setHotSheet({ ...hotSheet, manufacturer: m })}
                    objectValue={true}
                    error={errors.manufacturer}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-2">
                <ItemsSelector
                  name="Territories"
                  values={hotSheet.territories}
                  items={filteredTerritories}
                  fields={["name"]}
                  enable={filteredTerritories.length > 0}
                  onSave={handleTerritorySelection}
                  error={errors.territories}
                />
                <ItemsSelector
                  name="Representatives"
                  values={hotSheet.representatives}
                  items={filteredRepresentatives}
                  fields={["firstName", "lastName"]}
                  enable={filteredRepresentatives.length > 0}
                  onSave={(reps) => setHotSheet({ ...hotSheet, representatives: reps })}
                  error={errors.representatives}
                />
                <ItemsSelector
                  name="Banners"
                  values={hotSheet.banners}
                  items={filteredBanners}
                  fields={["name"]}
                  enable={filteredBanners.length > 0}
                  onSave={handleBannerSelection}
                  error={errors.banners}
                />
                <ItemsSelector
                  name="Stores"
                  values={hotSheet.stores}
                  items={filteredStores}
                  fields={["name", "number", "civicNo", "street", "city"]}
                  enable={filteredStores.length > 0}
                  onSave={(stores) => setHotSheet({ ...hotSheet, stores })}
                />
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <Priorities
                priorities={priorities}
                onAdd={addPriority}
                onUpdate={updatePriority}
                onDelete={deletePriority}
              />
            </div>
          </div>

        </Form>
      </div>
    </div>
  );
}

export default HotSheet;
